import { useTranslation } from "react-i18next";
import { z } from "zod";
import PublicShell from "./PublicShell";

type LegalKind = "privacy" | "terms";

const legalSectionSchema = z.object({
  title: z.string(),
  paragraphs: z.array(z.string()).optional(),
  items: z.array(z.string()).optional(),
  entries: z
    .array(z.object({ lead: z.string(), body: z.string() }))
    .optional(),
  closing: z.string().optional(),
});

const legalSectionsSchema = z.array(legalSectionSchema);

export default function LegalDocument({ kind }: { kind: LegalKind }) {
  const { t } = useTranslation();
  const prefix = `legal.${kind}`;
  const parsed = legalSectionsSchema.safeParse(
    t(`${prefix}.sections`, { returnObjects: true }),
  );
  const sections = parsed.success ? parsed.data : [];

  return (
    <PublicShell>
      <main className="px-6 py-16">
        <article className="mx-auto max-w-3xl">
          <header className="space-y-3 border-b border-border/60 pb-8">
            <p className="text-xs font-medium uppercase tracking-widest text-primary">
              {t(`${prefix}.effectiveDate`)}
            </p>
            <h1 className="text-3xl font-semibold tracking-tight">
              {t(`${prefix}.title`)}
            </h1>
            <p className="text-pretty text-muted-foreground leading-relaxed">
              {t(`${prefix}.intro`)}
            </p>
          </header>
          <div className="divide-y divide-border/60">
            {sections.map((section) => (
              <section key={section.title} className="space-y-4 py-8">
                <h2 className="text-xl font-semibold tracking-tight">
                  {section.title}
                </h2>
                {section.paragraphs?.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-pretty text-muted-foreground leading-relaxed"
                  >
                    {paragraph}
                  </p>
                ))}
                {section.items && section.items.length > 0 ? (
                  <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
                {section.entries && section.entries.length > 0 ? (
                  <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                    {section.entries.map((entry) => (
                      <li key={entry.lead}>
                        <strong className="text-foreground">{entry.lead}</strong>
                        {" — "}
                        {entry.body}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {section.closing ? (
                  <p className="text-pretty text-muted-foreground leading-relaxed">
                    {section.closing}
                  </p>
                ) : null}
              </section>
            ))}
          </div>
        </article>
      </main>
    </PublicShell>
  );
}
